import React, { useEffect } from 'react';
import { Autocomplete, Box, Button, ListItem } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';

import { cloudinaryImageUrl, CloudinaryConfig } from '../../../../Services/Cloudinary';
import ButtonWithLoader from '../../../ButtonWithLoader';
import Dialog from '../../../DesignSystem/Dialog';
import CardHeader from '../../../DesignSystem/CardHeader/CardHeader';
import useUsersQuery from '../../../../Hooks/useUsersQuery';
import TextFieldDefault from '../../Forms/TextFieldDefault';
import useAssignment from '../../../../Hooks/useAssignment';
import { Roles } from '../../../../Utils/Types';

export default function AssignmentModal({ open, onClose, post }: any) {
  const intl = useIntl();

  const { users, isFetching } = useUsersQuery('users', {
    strategy: 'replace',
    limit: 999,
    filter: {
      role: [Roles.CONTRIBUTOR, Roles.ADMINISTRATOR],
    },
    useQueryProps: {
      enabled: open,
    },
  });
  const defaultAssignment = post?.included?.assignment;
  const [value, setValue] = React.useState();

  useEffect(() => {
    setValue(users.find((user: any) => user.id === defaultAssignment?.user_id));
  }, [users]);

  const { handleSubmit, control, watch, setError } = useForm({
    reValidateMode: 'onChange',
    defaultValues: {
      post_record_id: parseInt(post?.id, 10),
      user_id: defaultAssignment?.user_id,
    },
  });

  const { assign, unassign } = useAssignment({
    assignmentId: defaultAssignment?.id,
    onSubmit: onClose,
    setError,
  });

  const userId = watch('user_id');

  const onSubmit = () => {
    if (userId) {
      return assign;
    }
    if (defaultAssignment?.id) {
      return unassign;
    }
    return onClose;
  };

  const dialogActions = (
    <>
      <Button variant="outlined" size="large" onClick={onClose} color="primary">
        <FormattedMessage id="BUTTONS.CANCEL" />
      </Button>
      <ButtonWithLoader
        isFetching={isFetching}
        variant="contained"
        size="large"
        onClick={handleSubmit(onSubmit())}
        color="primary"
        disabled={isFetching}
      >
        <FormattedMessage id="COMMENTS.CREATION.VALIDATE" />
      </ButtonWithLoader>
    </>
  );

  return (
    <Dialog
      id="assignment-form-dialog"
      label="assignment-form-dialog"
      title={intl.formatMessage({ id: 'ASSIGNMENT_MODAL.TITLE' })}
      open={open}
      onClose={onClose}
      actions={dialogActions}
    >
      <Box sx={{ mt: 1 }}>
        <form onSubmit={handleSubmit(userId ? assign : () => unassign(defaultAssignment?.id))}>
          <Controller
            control={control}
            name="user_id"
            render={({ field: { onChange, ...rest } }: any) => (
              <Autocomplete
                {...rest}
                loading={isFetching}
                id="assign-to-administrator"
                sx={{ width: 300 }}
                options={users}
                isOptionEqualToValue={(option: any, item: any) =>
                  parseInt(option.id, 10) === parseInt(item?.id, 10)
                }
                value={value}
                autoHighlight
                onChange={(_event: any, newValue: any) => {
                  setValue(newValue);
                  onChange(newValue?.id);
                }}
                getOptionLabel={(option: any) => option.full_name}
                getOptionKey={(option: any) => option.id}
                renderOption={(props: any, option: any) => (
                  <ListItem key={option.id} p={1} {...props}>
                    <CardHeader
                      profilePictureUrl={cloudinaryImageUrl(
                        option?.picture?.s3_key,
                        CloudinaryConfig.AVATAR_30,
                        option?.picture?.title,
                      )}
                      fullName={option.full_name}
                      avatarSize={30}
                      initials={option.initials}
                      alt={intl.formatMessage({ id: 'USER_AVATAR' })}
                    />
                  </ListItem>
                )}
                renderInput={(params: any) => (
                  <TextFieldDefault
                    {...params}
                    label={intl.formatMessage({ id: 'ASSIGNMENT_MODAL.CHOOSE_ADMIN' })}
                    inputProps={{
                      ...params.inputProps,
                    }}
                  />
                )}
              />
            )}
          />
        </form>
      </Box>
    </Dialog>
  );
}
